import { useCallback } from "react";
import { isNil } from "lodash";
import { transformVisitorStatus } from "@/helpers/helpers";
import { logger } from "@/services/LoggerService";

interface MixpanelEventData {
    [key: string]: any;
}

/** Values sent to Mixpanel as `notification_type` for in-app notification events */
export type MixpanelNotificationType =
    | "comment_reply"
    | "comment_like"
    | "new_follower"
    | "new_puzzle_followed";

interface AdditionalInfo {
    isAuthenticated?: boolean;
    visitorStatus?: string | null;
    pageName?: string;
    propertyUrl?: string | null;
    referringUrl?: string;
    source?: string;
    hasCommentHashtag?: boolean;
    comment_depth?: string;
    playable_property_id?: string | null;
    comment_type?: "general" | "score_reply" | "comment_reply" | string;
    character_count?: number;
    comment_text?: string | null;
    hashtagName?: string;
    hashtag?: string;
    user_id?: string | number | null;
    nickname?: string | null;
    email?: string | null;
    action?: string;
    reaction?: string;
    pageUrl?: string;
    guess_number?: number;
    guess_amount?: number;
    userScore?: string | number;
    location?: string;
    agentName?: string;
    agentMlsId?: string;
    clickType?: string;
    clickLocation?: string;
    externalUrl?: string | null;
    /**
     * Legacy property for deprecated **Auto-advance** events.
     * Kept on the type for clarity when reading historical payloads.
     */
    autoAdvanceType?: "click" | "countdown" | string;
    externalClickType?: "full" | "giveup" | string;
    profileViewed?: "own" | "other";
    puzzleUrl?: string | null;
    toggleTo?: "score" | "stats" | "back_to_listing";
    email_follower?: string | null;
    nickname_follower?: string | null;
    nickname_followed?: string | null;
    email_liker?: string | null;
    nickname_liker?: string | null;
    nickname_liked?: string | null;
    action_type?: "Like" | "Unlike" | string;
    content_type?: "puzzle" | "comment" | string;
    content_id?: string | null;
    like_count?: number;
    nickname_tapped?: string | null;
    type?: "Follow" | "Unfollow" | string;
    notificationType?: MixpanelNotificationType;
    notificationActorNickname?: string | null;
    dismiss_method?: "x_out" | "how_to_play";
    current_streak?: number;
    streak_change_type?: "incremented" | "reset" | "already_completed" | "guest_static";
    is_guest?: boolean;
    dismiss_type?: "timeout" | "tap_outside" | "x_button" | "save_clicked";
    time_on_modal_ms?: number;
    previous_streak?: number;
}

interface Mixpanel {
    track: (eventName: string, data: MixpanelEventData) => void;
}

declare global {
    interface Window {
        mixpanel?: Mixpanel;
    }
}

interface UseMixpanelEventReturn {
    sendEvent: (eventName: string, additionalInfo: AdditionalInfo) => void;
}

const useMixpanelEvent = (): UseMixpanelEventReturn => {
    /**
     * Sends an event to Mixpanel with additional information.
     *
     * @param eventName - The name of the event to be sent to Mixpanel.
     * @param additionalInfo - Additional information for constructing the event data.
     * @param additionalInfo.isAuthenticated - The authentication status of the user.
     * @param additionalInfo.visitorStatus - The visitor status (new-guest, repeat-guest, registered).
     * @param additionalInfo.pageName - The name of the page where the event occurred (e.g., "Property Feed - Trending", "Property Details").
     * @param additionalInfo.propertyUrl - The URL of the property being shared.
     * @param additionalInfo.referringUrl - The URL of the page that referred the user.
     * @param additionalInfo.source - The source of the registration (e.g., "social", "challenge", "daily").
     * @param additionalInfo.hasCommentHashtag - Whether the comment includes a hashtag.
     * @param additionalInfo.comment_depth - Thread depth (1st level, 2nd level, 3rd level).
     * @param additionalInfo.comment_type - Comment classification (general, score_reply, comment_reply).
     * @param additionalInfo.hashtagName - The name of the hashtag being created.
     * @param additionalInfo.hashtag - The hashtag being voted on.
     * @param additionalInfo.user_id - The user identifier.
     * @param additionalInfo.nickname - The user's nickname.
     * @param additionalInfo.email - The user's email address.
     * @param additionalInfo.action - The action performed (e.g., 'add', 'remove').
     * @param additionalInfo.reaction - The reaction (e.g., '😍 - U+1F60D').
     * @param additionalInfo.pageUrl - The URL of the page the user is on.
     * @param additionalInfo.guess_number - The guess attempt number in the puzzle flow.
     * @param additionalInfo.guess_amount - The numeric amount the user entered (for Mixpanel aggregations).
     * @param additionalInfo.userScore - The user score,
     * @param additionalInfo.location - The specific location where the event is triggered,
     * @param additionalInfo.agentName - The agent's full name (first name + last name).
     * @param additionalInfo.agentMlsId - The agent's MLS ID.
     * @param additionalInfo.clickType - The type of click (external link, instagram, tiktok, x, facebook, youtube, phone, website, custom).
     * @param additionalInfo.clickLocation - The location where the click occurred (property_page, daily_score_modal).
     *
     * @returns void
     */
    const sendEvent = useCallback((eventName: string, additionalInfo: AdditionalInfo): void => {
        try {
            const mixpanel = window?.mixpanel;
            if (!mixpanel) {
                console.warn("Mixpanel not available for tracking event:", eventName);
                logger.warn("Mixpanel not available for tracking event", {
                    eventName,
                    context: "useMixpanelEvent",
                });
                return;
            }

            const track = mixpanel.track;
            if (typeof track !== "function") {
                console.warn("Mixpanel track method not available");
                logger.warn("Mixpanel track method not available", {
                    eventName,
                    context: "useMixpanelEvent",
                });
                return;
            }

            const eventData: MixpanelEventData = {};

            const {
                isAuthenticated,
                visitorStatus,
                pageName,
                propertyUrl,
                referringUrl,
                source,
                hasCommentHashtag,
                comment_depth,
                playable_property_id,
                comment_type,
                character_count,
                comment_text,
                hashtagName,
                hashtag,
                user_id,
                nickname,
                email,
                action,
                reaction,
                pageUrl,
                guess_number,
                guess_amount,
                userScore,
                location,
                agentName,
                agentMlsId,
                clickType,
                clickLocation,
                externalUrl,
                autoAdvanceType,
                externalClickType,
                profileViewed,
                puzzleUrl,
                toggleTo,
                email_follower,
                nickname_follower,
                nickname_followed,
                email_liker,
                nickname_liker,
                nickname_liked,
                action_type,
                content_type,
                content_id,
                like_count,
                nickname_tapped,
                type,
                notificationType,
                notificationActorNickname,
                dismiss_method,
                current_streak,
                streak_change_type,
                is_guest,
                dismiss_type,
                time_on_modal_ms,
                previous_streak,
            } = additionalInfo;

            if (!isNil(isAuthenticated)) {
                let userStatus = "guest";

                if (isAuthenticated) {
                    userStatus = "registered";
                }

                eventData["user_status"] = userStatus;
            }

            if (!isNil(visitorStatus) && !isAuthenticated) {
                eventData["visitor_status"] = transformVisitorStatus(visitorStatus);
            }

            if (!isNil(pageName)) {
                eventData["page_name"] = pageName;
            }

            if (!isNil(propertyUrl)) {
                eventData["property_url"] = propertyUrl;
            }

            if (!isNil(referringUrl)) {
                eventData["referring_url"] = referringUrl;
            }

            if (!isNil(source)) {
                eventData["source"] = source;
            }

            if (!isNil(action)) {
                eventData["action"] = action;
            }

            if (!isNil(reaction)) {
                eventData["reaction"] = reaction;
            }

            if (!isNil(hasCommentHashtag)) {
                eventData["comment_hashtag"] = hasCommentHashtag ? "Y" : "N";
            }

            if (!isNil(comment_depth)) {
                eventData["comment_depth"] = comment_depth;
            }

            if (!isNil(playable_property_id)) {
                eventData["playable_property_id"] = playable_property_id;
            }

            if (!isNil(comment_type)) {
                eventData["comment_type"] = comment_type;
            }

            if (!isNil(character_count)) {
                eventData["character_count"] = character_count;
            }

            if (!isNil(comment_text)) {
                eventData["comment_text"] = comment_text;
            }

            if (!isNil(hashtagName)) {
                eventData["hashtag_name"] = hashtagName;
            }

            if (!isNil(hashtag)) {
                eventData["hashtag"] = hashtag;
            }

            if (!isNil(user_id)) {
                eventData["user_id"] = user_id;
            }

            if (!isNil(nickname)) {
                eventData["nickname"] = nickname;
            }

            if (!isNil(email)) {
                eventData["email"] = email;
            }

            if (!isNil(pageUrl)) {
                eventData["page_url"] = pageUrl;
            }

            if (!isNil(guess_number)) {
                eventData["guess_number"] = guess_number;
            }

            if (!isNil(guess_amount)) {
                eventData["guess_amount"] = guess_amount;
            }

            if (!isNil(userScore)) {
                eventData["user_score"] = userScore;
            }

            if (!isNil(location)) {
                eventData["location"] = location;
            }

            if (!isNil(agentName)) {
                eventData["agent_name"] = agentName;
            }

            if (!isNil(agentMlsId)) {
                eventData["agent_mls_id"] = agentMlsId;
            }

            if (!isNil(clickType)) {
                eventData["click_type"] = clickType;
            }

            if (!isNil(clickLocation)) {
                eventData["click_location"] = clickLocation;
            }

            if (!isNil(externalUrl)) {
                eventData["external_url"] = externalUrl;
            }

            if (!isNil(autoAdvanceType)) {
                eventData["auto_advance_type"] = autoAdvanceType;
            }

            if (!isNil(externalClickType)) {
                eventData["external_click_type"] = externalClickType;
            }

            if (!isNil(profileViewed)) {
                eventData["profile_viewed"] = profileViewed;
            }

            if (!isNil(puzzleUrl)) {
                eventData["puzzle_url"] = puzzleUrl;
            }

            if (!isNil(toggleTo)) {
                eventData["toggle_to"] = toggleTo;
            }

            if (!isNil(email_follower)) {
                eventData["email_follower"] = email_follower;
            }

            if (!isNil(nickname_follower)) {
                eventData["nickname_follower"] = nickname_follower;
            }

            if (!isNil(nickname_followed)) {
                eventData["nickname_followed"] = nickname_followed;
            }

            if (!isNil(email_liker)) {
                eventData["email_liker"] = email_liker;
            }

            if (!isNil(nickname_liker)) {
                eventData["nickname_liker"] = nickname_liker;
            }

            if (!isNil(nickname_liked)) {
                eventData["nickname_liked"] = nickname_liked;
            }

            if (!isNil(action_type)) {
                eventData["action_type"] = action_type;
            }

            if (!isNil(content_type)) {
                eventData["content_type"] = content_type;
            }

            if (!isNil(content_id)) {
                eventData["content_id"] = content_id;
            }

            if (!isNil(like_count)) {
                eventData["like_count"] = like_count;
            }

            if (!isNil(nickname_tapped)) {
                eventData["nickname_tapped"] = nickname_tapped;
            }

            if (!isNil(type)) {
                eventData["type"] = type;
            }

            if (!isNil(notificationType)) {
                eventData["notification_type"] = notificationType;
            }

            if (!isNil(notificationActorNickname)) {
                eventData["notification_actor_nickname"] = notificationActorNickname;
            }

            if (!isNil(dismiss_method)) {
                eventData["dismiss_method"] = dismiss_method;
            }

            if (!isNil(current_streak)) {
                eventData["current_streak"] = current_streak;
            }

            if (!isNil(streak_change_type)) {
                eventData["streak_change_type"] = streak_change_type;
            }

            if (!isNil(is_guest)) {
                eventData["is_guest"] = is_guest;
            }

            if (!isNil(dismiss_type)) {
                eventData["dismiss_type"] = dismiss_type;
            }

            if (!isNil(time_on_modal_ms)) {
                eventData["time_on_modal_ms"] = time_on_modal_ms;
            }

            if (!isNil(previous_streak)) {
                eventData["previous_streak"] = previous_streak;
            }

            // Send event to Mixpanel
            track.call(mixpanel, eventName, eventData);
        } catch (error) {
            console.error("Error tracking Mixpanel event:", error);
            logger.error("Error tracking Mixpanel event", error, {
                eventName,
                context: "useMixpanelEvent",
            });
        }
    }, []);

    return { sendEvent };
};

export default useMixpanelEvent;
